import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartOptions, TooltipItem } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import InfoTooltip from 'components/Dashboard/SpotCard/InfoTooltip/InfoTooltip';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import DoughnutSkeleton from 'components/Skeletons/DoughnutSkeleton';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { mockRentalStatsByTypeFull } from 'mocks/rentalStatsByType';
import { formatPercentage } from 'shared-ui';
import { RentalStatsByType } from 'types/CarTracking';
import doughnutGraphImg from 'assets/doughnutGraph.png';
import { getDriverTypeChartData, getDriverTypeTiles, hasDriverTypeData } from './driverTypesHelpers';
import styles from './DriverTypesWidget.module.css';

type DriverTypesWidgetProps = {
    spotUuid: string;
    isDemoMode?: boolean;
};

function DriverTypesLoadingSkeleton(): JSX.Element {
    return (
        <div className={styles.skeletonContainer} data-testid="driver-types-skeleton">
            <div className={styles.skeletonTitle}>
                <RectangleSkeleton width="120px" height="16px" />
            </div>
            <div className={styles.skeletonChart}>
                <DoughnutSkeleton />
            </div>
            <div className={styles.skeletonCards}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={`driver-type-skeleton-${index}`} className={styles.skeletonCard}>
                        <RectangleSkeleton width="72%" height="16px" />
                        <RectangleSkeleton width="28%" height="16px" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function DriverTypesWidget({ spotUuid, isDemoMode = false }: DriverTypesWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [report, setReport] = useState<RentalStatsByType | null>(null);

    const fetchDriverTypes = useCallback(async () => {
        if (!spotUuid) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const response = isDemoMode
                ? mockRentalStatsByTypeFull
                : await trackingClient.getRentalStatsByType(spotUuid);
            setReport(response);
        } catch (error) {
            if (HTTPError.isHTTPError(error)) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(error?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [isDemoMode, spotUuid]);

    useEffect(() => {
        fetchDriverTypes();
    }, [fetchDriverTypes]);

    const tiles = useMemo(() => (report ? getDriverTypeTiles(report) : []), [report]);
    const chartData = useMemo(() => getDriverTypeChartData(tiles), [tiles]);

    const options: ChartOptions<'doughnut'> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: '64%',
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label(tooltipItem: TooltipItem<'doughnut'>): string {
                            const tile = tiles[tooltipItem.dataIndex];
                            if (!tile) {
                                return '';
                            }
                            return `${tile.label}: ${formatPercentage(tile.percentage)}`;
                        },
                    },
                },
            },
        }),
        [tiles]
    );

    const isLoading = loading || !report;
    const showEmptyState = report && !hasDriverTypeData(report);
    const showChart = report && tiles.length > 0 && !showEmptyState;

    const content = useMemo(() => {
        if (errorMessage) {
            return <WidgetErrorMessage message={errorMessage} onRetry={fetchDriverTypes} />;
        }

        if (isLoading) {
            return <DriverTypesLoadingSkeleton />;
        }

        if (showEmptyState) {
            return (
                <div className={styles.content}>
                    <h4 className={styles.sectionTitle}>Currently Parked</h4>
                    <EmptyWidget imgUrl={doughnutGraphImg} message="Data will appear here once it's available." />
                </div>
            );
        }

        if (showChart) {
            return (
                <div className={styles.content}>
                    <h4 className={styles.sectionTitle}>Currently Parked</h4>
                    <div className={styles.chartContainer}>
                        <Doughnut
                            data={chartData}
                            options={options}
                            className={styles.chart}
                            data-testid="driver-types-doughnut-chart"
                        />
                    </div>
                    <div className={styles.tilesGrid}>
                        {tiles.map((tile) => (
                            <div key={tile.key} className={styles.tile}>
                                <div className={styles.tileLabelWrap}>
                                    <span
                                        className={styles.colorSwatch}
                                        style={{ backgroundColor: `var(${tile.colorToken})` }}
                                    />
                                    <div className={styles.tileText}>
                                        <span className={styles.tileLabel}>
                                            {tile.label}
                                            {tile.tooltip ? <InfoTooltip text={tile.tooltip} /> : null}
                                        </span>
                                        {tile.subtitle ? (
                                            <span className={styles.tileSubtitle}>{tile.subtitle}</span>
                                        ) : null}
                                    </div>
                                </div>
                                <span className={styles.tilePercent}>{formatPercentage(tile.percentage)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return null;
    }, [chartData, errorMessage, fetchDriverTypes, isLoading, options, showChart, showEmptyState, tiles]);

    return (
        <WidgetBox title="Driver Types">
            <div data-testid="driver-types-widget">{content}</div>
        </WidgetBox>
    );
}

export default DriverTypesWidget;
